import ru from "date-fns/locale/ru";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Controller, useForm } from "react-hook-form";
import { maintenanceType } from "../../../../enums";
import { useFetch } from "../../../../hooks/useFetch";
import { Maintenance } from "../../../../types/types";
import { Button } from "../../../../ui/Button";
import { DatePicker } from "../../../../ui/Datepicker/DatePicker";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../../../ui/Dialog";
import { Input } from "../../../../ui/Input/Input";
import { Label } from "../../../../ui/Label/Label";
import { ScrollArea } from "../../../../ui/ScrollArea/ScrollArea";
import { SelectButton } from "../../../../ui/SelectButton/SelectButton";
import { Textarea } from "../../../../ui/TextArea/Textarea";
registerLocale("es", ru);

type Props = {
  service: Maintenance;
  updateList: Function;
};

const EditService = ({
  service,
  updateList
}: Props) => {
  const maintenanceTypes = maintenanceType.map((type) => ({ label: t(`maintenance_${type}`), value: type }));
  const [errors, setErrors] = useState([]);

  const { handleSubmit, control } = useForm({
    defaultValues: {
      maintenance_name: service?.attributes.description,
      maintenance_type: {
        value: service?.attributes.maintenance_type,
        label: t("maintenance_" + service?.attributes.maintenance_type),
      },
      maintenance_time: service?.attributes.maintenance_time,
      maintenance_previous_date: service?.attributes.previous_maintenance_at
        ? new Date(service?.attributes.previous_maintenance_at)
        : new Date(),
      maintenance_description: service?.attributes.maintenance_description,
    },
  });

  const [{ response: updateResponse, isLoading: updateIsLoading, error: updateError }, doFetchItemUpdate] =
    useFetch(`/directories/equipment_items/${service.attributes.equipment_item_id}/maintenances/${service.id}`);

  const onUpdate = (formData: any) => {
    const maintenanceType = typeof formData.maintenance_type === "string"
      ? formData.maintenance_type
      : formData.maintenance_type.value;

    doFetchItemUpdate({
      method: "PUT",
      data: {
        data: {
          previous_maintenance_at: formData.maintenance_previous_date || new Date(),
          description: formData.maintenance_name || null,
          maintenance_time: formData.maintenance_time || null,
          maintenance_type: maintenanceType,
          maintenance_description: formData.maintenance_description || "",
        },
      },
    });
  };

  useEffect(() => {
    if (updateResponse) {
      updateList(updateResponse)
    }
  }, [updateResponse]);

  return (
    <ScrollArea className="w-full">
      <form onSubmit={handleSubmit(onUpdate)}>
        <div className="flex flex-col w-full pt-8 h-full gap-3">
          <Controller
            name="maintenance_name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div className="relative space-y-2">
                <Input {...field} label={`${t("maintenance_name")}*`} />
              </div>
            )}
          />
          <Controller
            name="maintenance_type"
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <div className="relative space-y-2">
                <Label>{t("maintenance_type")}*</Label>
                <SelectButton
                  label={"choose_maintenance_type"}
                  searchable={false}
                  options={maintenanceTypes}
                  value={value ? maintenanceTypes?.find((s) => s.value == String(value.value))! : null}
                  onChange={(e) => onChange(e?.value)}
                />
              </div>
            )}
          />
          <Controller
            name="maintenance_time"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div className="relative space-y-2">
                <Input {...field} label={`${t("maintenance_time")}*`} />
              </div>
            )}
          />
          <Controller
            name="maintenance_previous_date"
            control={control}
            rules={{ required: true }}
            render={({ field: {value, onChange} }) => (
              <div className="relative space-y-2">
                <Label>{t("maintenance_previous_date")}</Label>
                <DatePicker mode="single" algin="start" selected={value!} onSelect={onChange} />
              </div>
            )}
          />
          <Controller
            name="maintenance_description"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div className="relative space-y-2">
                <Label>{t("maintenance_description")}*</Label>
                <Textarea {...field} />
              </div>
            )}
          />
          <div className="flex justify-between items-center">
            <DialogClose>
              <Button variant={"secondary"}>
                {t("cancel")}
              </Button>
            </DialogClose>
            <Button type="submit">{t("save")}</Button>
          </div>
        </div>
      </form>
    </ScrollArea>
  );
};

export default EditService;
